import { useEffect, useState } from 'react'

import { useStopTurn } from '../lib/api'
import type { Chat, Topic } from '../lib/apiModels'

/**
 * Stop control for the turn running in a direct chat or open topic.
 *
 * Only rendered when the backend says the live turn is interruptible
 * (`topic.stoppable`) — that flag comes from `active_turns.via`, which is
 * written after the `turn_active` journal event, not the earlier `dispatched`
 * one. A turn that finishes between render and tap comes back
 * `stopped: false, reason: 'no_active_turn'`, which is shown as a plain note
 * rather than an error.
 */
export default function StopTurnBar({
  chatId,
  chat,
  topic,
}: {
  chatId: string
  chat: Chat
  topic: Topic | null
}) {
  const stopTurn = useStopTurn(chatId)
  const [note, setNote] = useState<string | null>(null)
  const topicId = topic?.id ?? null
  const conversation = topic ?? (chat.type === 'user-driven' ? chat : null)
  const stoppable = Boolean(!chat.archived && !topic?.closed && conversation?.stoppable)
  const sourceMessageId = conversation?.active_source_message_id
  const [requestedSource, setRequestedSource] = useState<string | null>(null)
  const stopping = stopTurn.isPending || Boolean(sourceMessageId && requestedSource === sourceMessageId)

  useEffect(() => { setNote(null); setRequestedSource(null) }, [topicId, sourceMessageId])

  if (!stoppable && !note) return null

  return (
    <div className="px-3 pt-2 flex items-center gap-2">
      {stoppable && <button
        type="button"
        data-testid="stop-turn"
        aria-label="Stop current response"
        disabled={stopping}
        onClick={async () => {
          setNote(null)
          try {
            const result = await stopTurn.mutateAsync(topicId)
            if (result.stopped) {
              setRequestedSource(sourceMessageId ?? null)
            } else {
              setNote(
                result.reason === 'no_active_turn'
                  ? 'That turn had already finished.'
                  : result.reason === 'not_stoppable'
                    ? 'This agent cannot be stopped mid-turn.'
                    : 'Stop failed — the turn is still running.',
              )
            }
          } catch {
            setNote('Stop failed — the lane controller did not answer.')
          }
        }}
        className="min-h-11 px-4 py-2 rounded-full text-sm font-medium border border-red-500/40 text-red-700 dark:text-red-300 bg-red-500/10 active:bg-red-500/20 disabled:opacity-50"
      >
        {stopping ? 'Stopping…' : 'Stop'}
      </button>}
      {note && <span role="status" className="text-xs text-stone-600 dark:text-neutral-400">{note}</span>}
    </div>
  )
}
